import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo } from 'react';

import { toast } from 'design-system';

export interface SnackbarMessage {
  loading: string;
  success: string;
  error: string;
}

interface SnackbarNotificationsContextValue {
  showLoadingSnackbar: (requestKey: string, message: string) => string;
  showSuccessSnackbar: (requestKey: string, message: string) => string;
  showErrorSnackbar: (requestKey: string, message: string, autoHideDuration?: number) => string;
  dismissNotification: (requestKey: string) => void;
}

const SnackbarNotificationsContext = createContext<SnackbarNotificationsContextValue | undefined>(
  undefined
);

interface SnackbarNotificationsProviderProps {
  children: ReactNode;
}

export const SnackbarNotificationsProvider = ({ children }: SnackbarNotificationsProviderProps) => {
  const showLoadingSnackbar = useCallback(
    (requestKey: string, message: string) =>
      toast.add({ id: requestKey, title: message, type: 'loading', timeout: 0 }),
    []
  );

  const showSuccessSnackbar = useCallback(
    (requestKey: string, message: string) =>
      toast.add({ id: requestKey, title: message, type: 'success', timeout: 5000 }),
    []
  );

  const showErrorSnackbar = useCallback(
    (requestKey: string, message: string, autoHideDuration?: number) =>
      toast.add({
        id: requestKey,
        title: message,
        type: 'error',
        timeout: autoHideDuration ?? 0
      }),
    []
  );

  const dismissNotification = useCallback((requestKey: string) => {
    toast.close(requestKey);
  }, []);

  const value: SnackbarNotificationsContextValue = useMemo(
    () => ({
      showLoadingSnackbar,
      showSuccessSnackbar,
      showErrorSnackbar,
      dismissNotification
    }),
    [dismissNotification, showErrorSnackbar, showLoadingSnackbar, showSuccessSnackbar]
  );

  return <SnackbarNotificationsContext value={value}>{children}</SnackbarNotificationsContext>;
};

export const useSnackbarNotifications = () => {
  const context = useContext(SnackbarNotificationsContext);
  if (context === undefined) {
    throw new Error('useSnackbarNotifications must be used within a SnackbarNotificationsProvider');
  }
  return context;
};
